import { Injectable, inject } from '@angular/core';
import { Borders, Workbook } from 'exceljs';
import jsPDF from 'jspdf';
import autoTable, { RowInput } from 'jspdf-autotable';
import { TableRow } from '../../models/lab-test';
import { DecontaminationReportData, Vw_DecontaminationUncontaminatedData } from '../../liveoil-decontamination/models/decontamination';
import { reportConstants, reportConstantsMessage } from '../../common/constants';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';
import { setNumericOrTextCellValue } from './report.cellFormat.util';

@Injectable({
    providedIn: 'root'
})
export class ReportPDFDecontaminationRenderService {
    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);

    Decontamination(doc: jsPDF, marginSize: number, decontExp: DecontaminationReportData, wellName: string, sampleName: string): void {
        if (decontExp !== undefined && decontExp !== null) {
            const val = decontExp;
            marginSize = 40;
            const formatedData = this.FormatTableDataUncontaminated(decontExp.vw_DecontaminationUncontaminated);
            doc.addPage();
            doc.setFontSize(12);
            doc.setFont(reportConstants.eMprintCondensedBold);
            let currentY = 30;
            doc.text(wellName + ' ' + sampleName, marginSize, currentY);
            currentY += 5;
            doc.setFontSize(10);
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(reportConstants.residualOil + ': ' + this.formatPercentageDisplay(decontExp.vw_DecontaminationProperties?.residualOilContaminantWtPct) + ' ' + reportConstants.wtContaminant, marginSize, currentY);
            currentY += 5;
            doc.text(reportConstants.liveFluid + ': ' + this.formatPercentageDisplay(decontExp.vw_DecontaminationProperties?.liveFluidContaminantWtPct) + ' ' + reportConstants.wtContaminant, marginSize, currentY);
            currentY += 5;
            let tableStartY = currentY;
            autoTable(doc, {
                head: [
                    [
                        {
                            content: '',
                            colSpan: 4,
                            styles: { valign: reportConstants.middle, halign: reportConstants.center }
                        },
                        { content: reportConstants.calculatedUncontaminatedCompositions, colSpan: 5, styles: { halign: reportConstants.center } }
                    ],
                    [
                        {
                            content: '',
                            colSpan: 2,
                            styles: { valign: reportConstants.middle, halign: reportConstants.center }
                        },
                        {
                            content: reportConstants.contaminant,
                            styles: { halign: reportConstants.center }
                        },
                        { content: '' },
                        { content: reportConstants.residualOil, colSpan: 2, styles: { halign: reportConstants.center } },
                        { content: '' },
                        { content: reportConstants.recombinedFluid, colSpan: 2, styles: { halign: reportConstants.center } }
                    ],
                    [
                        {
                            content: reportConstants.component,
                            colSpan: 2,
                            styles: { valign: reportConstants.middle, halign: reportConstants.center }
                        },
                        { content: reportConstants.weight, styles: { halign: reportConstants.center } },
                        { content: '' },
                        { content: reportConstants.weight, styles: { halign: reportConstants.center } },
                        { content: reportConstants.molePercent, styles: { halign: reportConstants.center } },
                        { content: '' },
                        { content: reportConstants.weight, styles: { halign: reportConstants.center } },
                        { content: reportConstants.molePercent, styles: { halign: reportConstants.center } }
                    ]
                ],
                body: formatedData as RowInput[],
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize },
                styles: {
                    fontSize: 8,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    lineColor: [0, 0, 0],
                    lineWidth: 0
                },
                headStyles: {
                    fontSize: 8,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    fontStyle: reportConstants.normal,
                    font: reportConstants.eMprintCondensedBold,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: 0
                },
                bodyStyles: {
                    fontSize: 8,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: 0
                },
                columnStyles: {
                    3: { cellWidth: 1 },
                    6: { cellWidth: 1 }
                },
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && (data.column.index === 0 || data.column.index === 1)) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, column, doc, section } = data;
                    if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;

                        doc.setDrawColor(255, 255, 255);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y, x + w, y);
                        doc.line(x, y + h, x + w, y + h);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                    }
                    else if (section === reportConstants.head && row.index === 1 && column.index === 0) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;

                        doc.setDrawColor(255, 255, 255);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y, x + w, y);
                        doc.line(x, y + h, x + w, y + h);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                    }
                    else {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);
                    }

                    if (section === reportConstants.head && row.index === 2) {
                        doc.setDrawColor(0, 0, 0);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.line(x, y + h - 0.5, x + w, y + h - 0.5);
                        doc.line(x, y + h, x + w, y + h);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                    }
                }
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

            doc.addPage();
            const topMargin = 21;
            doc.setFontSize(14);
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(`${wellName} ${sampleName}`, 25, topMargin);
            doc.setFontSize(12);
            doc.setFont(reportConstants.eMprintCondensedRegular);

            const leftX = 35;
            const rightX = 120;
            const yStart = topMargin + 10;
            const lineHeight = 6;

            let yLeft = yStart;
            const firstTableRows = [
                [reportConstants.residualOilMolContaminant, this.formatPercentageDisplay(decontExp.vw_DecontaminationContaminated?.residualOilMolPctContaminant)],
                [reportConstants.residualOilWtContaminant, this.formatPercentageDisplay(decontExp.vw_DecontaminationContaminated?.residualOilWtPctContaminant)],
                [reportConstants.liveFluidMolContaminant, this.formatPercentageDisplay(decontExp.vw_DecontaminationContaminated?.liveFluidMolPctContaminant)],
                [reportConstants.liveFluidWtContaminant, this.formatPercentageDisplay(decontExp.vw_DecontaminationContaminated?.liveFluidWtPctContaminant)]
            ];

            let yRight = yStart;
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.setFontSize(12);
            doc.text(reportConstants.contaminantFluidProperties, rightX, yRight);
            doc.setFont(reportConstants.eMprintCondensedRegular);
            yRight += lineHeight;

            const contaminantRows = [
                [reportConstants.contaminantDensity, `${decontExp.vw_DecontaminationContaminated?.contaminantDensity ?? ''}`, reportConstants.gcc],
                [reportConstants.contaminantMolWt, `${decontExp.vw_DecontaminationContaminated?.contaminantMolWt ?? ''}`, reportConstants.gmol]
            ];

            const contaminatedRows = [
                [reportConstants.liveFluidMolWt, `${decontExp.vw_DecontaminationContaminated?.liveFluidMolWt ?? ''}`, reportConstants.gmol],
                [reportConstants.residualOilMolWt, `${decontExp.vw_DecontaminationContaminated?.residualOilMolWt ?? ''}`, reportConstants.gmol],
                [reportConstants.residualOilGravityDec, `${decontExp.vw_DecontaminationContaminated?.residualOilGravity ?? ''}`, reportConstants.api],
                [reportConstants.gorSSFVol, `${decontExp.vw_DecontaminationContaminated?.gorSSFVol ?? ''}`, reportConstants.scfStb]
            ];

            const uncontaminatedRows = [
                [reportConstants.liveFluidMolWt, `${decontExp.vw_DecontaminationContaminated?.liveFluidMolWtUncontaminated ?? ''}`, reportConstants.gmol],
                [reportConstants.residualOilMolWt, `${decontExp.vw_DecontaminationContaminated?.residualOilMolWtUncontaminated ?? ''}`, reportConstants.gmol],
                [reportConstants.residualOilGravityDec, `${decontExp.vw_DecontaminationContaminated?.residualOilGravityUncontaminated ?? ''}`, reportConstants.api],
                [reportConstants.gorSSFVol, `${decontExp.vw_DecontaminationContaminated?.gorSSFVolUncontaminated ?? ''}`, reportConstants.scfStb]
            ];

            const allLeftKeys = [...firstTableRows.map(([k]) => k), ...contaminatedRows.map(([k]) => k)];
            const maxKeyWidthLeftCombined = Math.max(...allLeftKeys.map(k => doc.getTextWidth(k)));
            const colonXLeftShared = leftX + maxKeyWidthLeftCombined + 3;
            const valueXLeftShared = colonXLeftShared + 6;
            const unitXLeftShared = valueXLeftShared + 15;

            const allRightKeys = [...contaminantRows.map(([k]) => k), ...uncontaminatedRows.map(([k]) => k)];
            const maxKeyWidthRightCombined = Math.max(...allRightKeys.map(k => doc.getTextWidth(k)));
            const colonXRightShared = rightX + maxKeyWidthRightCombined + 3;
            const valueXRightShared = colonXRightShared + 6;
            const unitXRightShared = valueXRightShared + 15;

            firstTableRows.forEach(([key, value]) => {
                const numericPart = value.replace(reportConstants.percentage, '').trim();
                const numericWidth = doc.getTextWidth(numericPart);
                doc.text(key, colonXLeftShared - 3 - doc.getTextWidth(key), yLeft);
                doc.text(':', colonXLeftShared, yLeft);
                doc.text(numericPart, valueXLeftShared, yLeft);
                doc.text(reportConstants.percentage, valueXLeftShared + numericWidth + 1, yLeft);
                yLeft += lineHeight;
            });

            contaminantRows.forEach(([key, value, unit]) => {
                doc.text(key, colonXRightShared - 3 - doc.getTextWidth(key), yRight);
                doc.text(':', colonXRightShared, yRight);
                doc.text(value, valueXRightShared, yRight);
                doc.text(unit, unitXRightShared, yRight);
                yRight += lineHeight;
            });

            const nextRowStartY = Math.max(yLeft, yRight) + 5;

            let yLeftRow2 = nextRowStartY;
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(reportConstants.contaminatedFluidProperties, leftX, yLeftRow2);
            doc.setFont(reportConstants.eMprintCondensedRegular);
            yLeftRow2 += lineHeight;

            contaminatedRows.forEach(([key, value, unit]) => {
                doc.text(key, colonXLeftShared - 3 - doc.getTextWidth(key), yLeftRow2);
                doc.text(':', colonXLeftShared, yLeftRow2);
                doc.text(value, valueXLeftShared, yLeftRow2);
                doc.text(unit, unitXLeftShared, yLeftRow2);
                yLeftRow2 += lineHeight;
            });

            let yRightRow2 = nextRowStartY;
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(reportConstants.uncontaminatedFluidProperties, rightX, yRightRow2);
            doc.setFont(reportConstants.eMprintCondensedRegular);
            yRightRow2 += lineHeight;

            uncontaminatedRows.forEach(([key, value, unit]) => {
                doc.text(key, colonXRightShared - 3 - doc.getTextWidth(key), yRightRow2);
                doc.text(':', colonXRightShared, yRightRow2);
                doc.text(value, valueXRightShared, yRightRow2);
                doc.text(unit, unitXRightShared, yRightRow2);
                yRightRow2 += lineHeight;
            });

            if (val.deadOilGraphData.length > 0) {
                const pageWidth = doc.internal.pageSize.getWidth();
                const imageWidth = pageWidth * 0.7;
                const imageHeight = imageWidth * 0.5;

                const xPos = (pageWidth - imageWidth) / 2;
                let yPos = currentY + 55;

                doc.addImage(
                    val.deadOilGraphData[0],
                    reportConstants.jpeg,
                    xPos,
                    yPos,
                    imageWidth,
                    imageHeight,
                    undefined,
                    'FAST'
                );

                yPos += imageHeight + 5;

                doc.addImage(
                    val.deadOilGraphData[1],
                    reportConstants.jpeg,
                    xPos,
                    yPos,
                    imageWidth,
                    imageHeight,
                    undefined,
                    'FAST'
                );

            }

        }
    }

    async GenerateDecontamination(workbook: Workbook, worksheetName: string, docontExp: DecontaminationReportData, wellName: string, sampleName: string, subHeader: string) {
        try {
            const compAnalysisWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle: Partial<Borders> = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            compAnalysisWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(compAnalysisWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 1,
                scale: 50,
            });

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            compAnalysisWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(compAnalysisWorksheet, subHeader, reportConstants.c1, reportConstants.j1);

            const headerStartRow = 4;

            compAnalysisWorksheet.mergeCells(`B${headerStartRow}`, `G${headerStartRow}`);
            const wellboreCell = compAnalysisWorksheet.getCell(`B${headerStartRow}`);
            wellboreCell.value = `${wellName} ${sampleName}`;
            wellboreCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            wellboreCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            compAnalysisWorksheet.mergeCells(`B${headerStartRow + 1}`, `G${headerStartRow + 1}`);
            const residualOilCell = compAnalysisWorksheet.getCell(`B${headerStartRow + 1}`);
            residualOilCell.value = `${reportConstants.residualOil} : ${docontExp.vw_DecontaminationProperties?.residualOilContaminantWtPct} ${reportConstants.wtContaminant}`;
            residualOilCell.font = { name: reportConstants.eMprintCondensed, size: 12 };
            residualOilCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            compAnalysisWorksheet.mergeCells(`B${headerStartRow + 2}`, `G${headerStartRow + 2}`);
            const liveFluidCell = compAnalysisWorksheet.getCell(`B${headerStartRow + 2}`);
            liveFluidCell.value = `${reportConstants.liveFluid} : ${docontExp.vw_DecontaminationProperties?.liveFluidContaminantWtPct} ${reportConstants.wtContaminant}`;
            liveFluidCell.font = { name: reportConstants.eMprintCondensed, size: 12 };
            liveFluidCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            compAnalysisWorksheet.getColumn(reportConstants.c).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.d).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.e).width = 16;
            compAnalysisWorksheet.getColumn(reportConstants.fChar).width = 1;
            compAnalysisWorksheet.getColumn(reportConstants.gColumn).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.h).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.i).width = 1;
            compAnalysisWorksheet.getColumn(reportConstants.j).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.k).width = 11;

            const tableStartRow = headerStartRow + 3;
            compAnalysisWorksheet.getRow(tableStartRow).height = 20;
            compAnalysisWorksheet.getRow(tableStartRow + 1).height = 20;

            compAnalysisWorksheet.spliceRows(tableStartRow + 1, 0, []);
            compAnalysisWorksheet.getRow(tableStartRow + 2).height = 20;
            compAnalysisWorksheet.getCell(`F${tableStartRow + 1}`).border = {};
            compAnalysisWorksheet.getCell(`I${tableStartRow + 1}`).border = {};

            compAnalysisWorksheet.mergeCells(`G${tableStartRow}`, `K${tableStartRow}`);
            const textheaderCell = compAnalysisWorksheet.getCell(`G${tableStartRow}`);
            textheaderCell.value = reportConstants.calculatedUncontaminatedCompositions;
            textheaderCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textheaderCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textheaderCell.border = borderStyle;
            [reportConstants.gColumn, reportConstants.h, reportConstants.j, reportConstants.k].forEach(col => compAnalysisWorksheet.getColumn(col).width = 12);

            compAnalysisWorksheet.mergeCells(`E${tableStartRow + 1}`);
            const textFlashedLiquidCell = compAnalysisWorksheet.getCell(`E${tableStartRow + 1}`);
            textFlashedLiquidCell.value = reportConstants.contaminant;
            textFlashedLiquidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashedLiquidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFlashedLiquidCell.border = borderStyle;

            const headerCellH = compAnalysisWorksheet.getCell(`F${tableStartRow + 1}`);
            headerCellH.border = borderStyle;

            compAnalysisWorksheet.mergeCells(`G${tableStartRow + 1}`, `H${tableStartRow + 1}`);
            const textFlashedGasCell = compAnalysisWorksheet.getCell(`H${tableStartRow + 1}`);
            textFlashedGasCell.value = reportConstants.residualOil;
            textFlashedGasCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashedGasCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFlashedGasCell.border = borderStyle;

            const headerCellK = compAnalysisWorksheet.getCell(`I${tableStartRow + 1}`);
            headerCellK.border = borderStyle;

            compAnalysisWorksheet.mergeCells(`J${tableStartRow + 1}`, `K${tableStartRow + 1}`);
            const textWholeFluidCell = compAnalysisWorksheet.getCell(`K${tableStartRow + 1}`);
            textWholeFluidCell.value = reportConstants.recombinedFluid;
            textWholeFluidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textWholeFluidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textWholeFluidCell.border = borderStyle;

            compAnalysisWorksheet.mergeCells(`B${tableStartRow + 2}`, `D${tableStartRow + 2}`);
            const textComponentCell = compAnalysisWorksheet.getCell(`B${tableStartRow + 2}`);
            textComponentCell.value = reportConstants.component;
            textComponentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textComponentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textComponentCell.border = borderStyle;
            [reportConstants.b, reportConstants.c, reportConstants.d].forEach(col => {
                compAnalysisWorksheet.getColumn(col).width = 13;
            });

            const textLiquidWeightPercentCell = compAnalysisWorksheet.getCell(`E${tableStartRow + 2}`);
            textLiquidWeightPercentCell.value = reportConstants.weight;
            textLiquidWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textLiquidWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textLiquidWeightPercentCell.border = borderStyle;

            const subHeaderCellH = compAnalysisWorksheet.getCell(`F${tableStartRow + 2}`);
            subHeaderCellH.border = borderStyle;

            const textGasWeightPercentCell = compAnalysisWorksheet.getCell(`G${tableStartRow + 2}`);
            textGasWeightPercentCell.value = reportConstants.weight;
            textGasWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textGasWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textGasWeightPercentCell.border = borderStyle;

            const textGasMolePercentCell = compAnalysisWorksheet.getCell(`H${tableStartRow + 2}`);
            textGasMolePercentCell.value = reportConstants.molePercent;
            textGasMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textGasMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textGasMolePercentCell.border = borderStyle;

            const subHeaderCellK = compAnalysisWorksheet.getCell(`I${tableStartRow + 2}`);
            subHeaderCellK.border = borderStyle;

            const textFluidWeightPercentCell = compAnalysisWorksheet.getCell(`J${tableStartRow + 2}`);
            textFluidWeightPercentCell.value = reportConstants.weight;
            textFluidWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFluidWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFluidWeightPercentCell.border = borderStyle;

            const textFluidMolePercentCell = compAnalysisWorksheet.getCell(`K${tableStartRow + 2}`);
            textFluidMolePercentCell.value = reportConstants.molePercent;
            textFluidMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFluidMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFluidMolePercentCell.border = borderStyle;

            const dataList = docontExp.vw_DecontaminationUncontaminated as Vw_DecontaminationUncontaminatedData[];

            for (let i = 0; i < dataList.length; i++) {
                const data = dataList[i];

                const componentCell = compAnalysisWorksheet.getCell(`B${tableStartRow + 3 + i}`);
                componentCell.value = data.component;
                componentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
                componentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                componentCell.border = borderStyle;

                compAnalysisWorksheet.mergeCells(`C${tableStartRow + 3 + i}`, `D${tableStartRow + 3 + i}`);
                const componentNameCell = compAnalysisWorksheet.getCell(`C${tableStartRow + 3 + i}`);
                componentNameCell.value = data.componentName;
                componentNameCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
                componentNameCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                componentNameCell.border = borderStyle;

                const contaminantCell = compAnalysisWorksheet.getCell(`E${tableStartRow + 3 + i}`);
                setNumericOrTextCellValue(contaminantCell, data.contaminantWeightPct != null ? data.contaminantWeightPct.toFixed(3) : '');
                contaminantCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                contaminantCell.border = borderStyle;
                contaminantCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };

                const residualOilWeightCell = compAnalysisWorksheet.getCell(`G${tableStartRow + 3 + i}`);
                setNumericOrTextCellValue(residualOilWeightCell, data.residualOilUncontaminatedWeightPct != null ? data.residualOilUncontaminatedWeightPct.toFixed(3) : '');
                residualOilWeightCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                residualOilWeightCell.border = borderStyle;
                residualOilWeightCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };

                const residualOilMoleCell = compAnalysisWorksheet.getCell(`H${tableStartRow + 3 + i}`);
                setNumericOrTextCellValue(residualOilMoleCell, data.residualOilUncontaminatedMolePct != null ? data.residualOilUncontaminatedMolePct.toFixed(3) : '');
                residualOilMoleCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                residualOilMoleCell.border = borderStyle;
                residualOilMoleCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };

                const recombinedWeightCell = compAnalysisWorksheet.getCell(`J${tableStartRow + 3 + i}`);
                setNumericOrTextCellValue(recombinedWeightCell, data.recombinedFluidUncontaminatedWeightPct != null ? data.recombinedFluidUncontaminatedWeightPct.toFixed(3) : '');
                recombinedWeightCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                recombinedWeightCell.border = borderStyle;
                recombinedWeightCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };

                const recombinedMoleCell = compAnalysisWorksheet.getCell(`K${tableStartRow + 3 + i}`);
                setNumericOrTextCellValue(recombinedMoleCell, data.recombinedFluidUncontaminatedMolePct != null ? data.recombinedFluidUncontaminatedMolePct.toFixed(3) : '');
                recombinedMoleCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                recombinedMoleCell.border = borderStyle;
                recombinedMoleCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
            }
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }

    async GenerateDecontaminationComp(
        workbook: Workbook,
        worksheetName: string,
        docontExp: DecontaminationReportData,
        wellName: string,
        sampleName: string,
        subHeader: string
    ) {
        try {
            const ws = workbook.addWorksheet(worksheetName);

            ws.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(ws, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 1,
                scale: 65,
            });

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({ base64: exxonLogo, extension: reportConstants.png });
         
            ws.addImage(exxonLogoId, { tl: { col: 0, row: 0 }, ext: { width: 151, height: 31 } });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(ws, subHeader, reportConstants.c1, reportConstants.h1);

            ws.getColumn(reportConstants.c).width = 25;
            ws.getColumn(reportConstants.d).width = 15;
            ws.getColumn(reportConstants.e).width = 12;
            ws.getColumn(reportConstants.fChar).width = 25;
            ws.getColumn(reportConstants.gColumn).width = 15;
            ws.getColumn(reportConstants.h).width = 12;

            const headerRow = 5;

            ws.mergeCells(`B${headerRow}`, `E${headerRow + 1}`);
            const wellInfoCell = ws.getCell(`B${headerRow}`);
            wellInfoCell.value = `${wellName} ${sampleName}`;
            wellInfoCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            wellInfoCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.top };

            const contaminantDetails: [string, number | null | undefined][] = [
                [reportConstants.residualOilMolContaminant, docontExp.vw_DecontaminationContaminated?.residualOilMolPctContaminant ?? null],
                [reportConstants.residualOilWtContaminant, docontExp.vw_DecontaminationContaminated?.residualOilWtPctContaminant ?? null],
                [reportConstants.liveFluidMolContaminant, docontExp.vw_DecontaminationContaminated?.liveFluidMolPctContaminant ?? null],
                [reportConstants.liveFluidWtContaminant, docontExp.vw_DecontaminationContaminated?.liveFluidWtPctContaminant ?? null]
            ];

            const formatPercentageDisplay = (value: number | null | undefined) => {
                if (value == null) return '';
                const numericValue = Number(value);
                if (Number.isInteger(numericValue)) {
                    return `${numericValue.toFixed(1)}%`;
                }
                return `${numericValue}%`;
            };

            contaminantDetails.forEach((row, i) => {
                const [prop, value] = row;
                const r = headerRow + 2 + i;
                ws.getCell(`C${r}`).value = `${prop}:`;
                ws.getCell(`D${r}`).value = formatPercentageDisplay(value ?? null);
                ws.getCell(`C${r}`).alignment = { horizontal: reportConstants.right };
                ws.getCell(`D${r}`).alignment = { horizontal: reportConstants.center };
                ws.getCell(`C${r}`).font = { name: reportConstants.eMprintCondensed, size: 12 };
                ws.getCell(`D${r}`).font = { name: reportConstants.eMprintCondensed, size: 12 };
            });

            ws.mergeCells(`F${headerRow}`, `H${headerRow + 1}`);
            const contaminantTitleCell = ws.getCell(`F${headerRow}`);
            contaminantTitleCell.value = reportConstants.contaminantFluidProperties;
            contaminantTitleCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            contaminantTitleCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };

            const contaminantProps = [
                [reportConstants.contaminantDensity, docontExp.vw_DecontaminationContaminated?.contaminantDensity, reportConstants.gcc],
                [reportConstants.contaminantMolWt, docontExp.vw_DecontaminationContaminated?.contaminantMolWt, reportConstants.gmol]
            ];

            contaminantProps.forEach((row, i) => {
                const [prop, value, unit] = row;
                const r = headerRow + 2 + i;
                ws.getCell(`F${r}`).value = `${prop}:`;
                setNumericOrTextCellValue(ws.getCell(`G${r}`), value ?? '');
                ws.getCell(`H${r}`).value = unit;
                [reportConstants.fChar, reportConstants.gColumn, reportConstants.h].forEach((col, idx) => {
                    ws.getCell(`${col}${r}`).alignment = idx === 0 ? { horizontal: reportConstants.right } : idx === 1 ? { horizontal: reportConstants.center } : { horizontal: reportConstants.left };
                    ws.getCell(`${col}${r}`).font = { name: reportConstants.eMprintCondensed, size: 12 };
                });
            });

            const contaminatedStartRow = headerRow + 7;
            ws.mergeCells(`C${contaminatedStartRow}`, `E${contaminatedStartRow + 1}`);
            const contaminatedTitleCell = ws.getCell(`C${contaminatedStartRow}`);
            contaminatedTitleCell.value = reportConstants.contaminatedFluidProperties;
            contaminatedTitleCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            contaminatedTitleCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };

            const contaminatedProps = [
                [reportConstants.liveFluidMolWt, docontExp.vw_DecontaminationContaminated?.liveFluidMolWt, reportConstants.gmol],
                [reportConstants.residualOilMolWt, docontExp.vw_DecontaminationContaminated?.residualOilMolWt, reportConstants.gmol],
                [reportConstants.residualOilGravityDec, docontExp.vw_DecontaminationContaminated?.residualOilGravity, reportConstants.api],
                [reportConstants.gorSSFVol, docontExp.vw_DecontaminationContaminated?.gorSSFVol, reportConstants.scfStb]
            ];

            contaminatedProps.forEach((row, i) => {
                const [prop, value, unit] = row;
                const r = contaminatedStartRow + 2 + i;
                ws.getCell(`C${r}`).value = `${prop}:`;
                setNumericOrTextCellValue(ws.getCell(`D${r}`), value ?? '');
                ws.getCell(`E${r}`).value = unit;
                [reportConstants.c, reportConstants.d, reportConstants.e].forEach((col, idx) => {
                    ws.getCell(`${col}${r}`).alignment = idx === 0 ? { horizontal: reportConstants.right } : idx === 1 ? { horizontal: reportConstants.center } : { horizontal: reportConstants.left };
                    ws.getCell(`${col}${r}`).font = { name: reportConstants.eMprintCondensed, size: 12 };
                });
            });

            ws.mergeCells(`F${contaminatedStartRow}`, `H${contaminatedStartRow + 1}`);
            const uncontaminatedTitleCell = ws.getCell(`F${contaminatedStartRow}`);
            uncontaminatedTitleCell.value = reportConstants.uncontaminatedFluidProperties;
            uncontaminatedTitleCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            uncontaminatedTitleCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };

            const uncontaminatedProps = [
                [reportConstants.liveFluidMolWt, docontExp.vw_DecontaminationContaminated?.liveFluidMolWtUncontaminated, reportConstants.gmol],
                [reportConstants.residualOilMolWt, docontExp.vw_DecontaminationContaminated?.residualOilMolWtUncontaminated, reportConstants.gmol],
                [reportConstants.residualOilGravityDec, docontExp.vw_DecontaminationContaminated?.residualOilGravityUncontaminated, reportConstants.api],
                [reportConstants.gorSSFVol, docontExp.vw_DecontaminationContaminated?.gorSSFVolUncontaminated, reportConstants.scfStb]
            ];

            uncontaminatedProps.forEach((row, i) => {
                const [prop, value, unit] = row;
                const r = contaminatedStartRow + 2 + i;
                ws.getCell(`F${r}`).value = `${prop}:`;
                setNumericOrTextCellValue(ws.getCell(`G${r}`), value ?? '');
                ws.getCell(`H${r}`).value = unit;
                [reportConstants.fChar, reportConstants.gColumn, reportConstants.h].forEach((col, idx) => {
                    ws.getCell(`${col}${r}`).alignment = idx === 0 ? { horizontal: reportConstants.right } : idx === 1 ? { horizontal: reportConstants.center } : { horizontal: reportConstants.left };
                    ws.getCell(`${col}${r}`).font = { name: reportConstants.eMprintCondensed, size: 12 };
                });
            });

            const deadOilGraphId = workbook.addImage({ base64: docontExp.deadOilGraphData[0], extension: reportConstants.png });
            ws.addImage(deadOilGraphId, { tl: { col: 2, row: contaminatedStartRow + 7 }, ext: { width: 650, height: 300 } });

            const liveFluidGraphId = workbook.addImage({ base64: docontExp.deadOilGraphData[1], extension: reportConstants.png });
            ws.addImage(liveFluidGraphId, { tl: { col: 2, row: contaminatedStartRow + 27 }, ext: { width: 650, height: 300 } });
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }

    private FormatTableDataUncontaminated(report: Vw_DecontaminationUncontaminatedData[]): TableRow[] {
        const grouped = new Map();

        report.forEach((val: Vw_DecontaminationUncontaminatedData) => {
            const key = val.component ?? '';
            if (!grouped.has(key)) grouped.set(key, []);
            grouped.get(key).push(val);
        });

        const formattedRows: TableRow[] = [];

        for (const [component, rows] of grouped.entries()) {
            rows.forEach((val: Vw_DecontaminationUncontaminatedData, index: number) => {
                const row = [];

                if (index === 0) {
                    row.push({
                        content: component,
                        rowSpan: rows.length,
                        styles: { valign: reportConstants.middle, halign: reportConstants.center }
                    });
                }
                row.push(val.componentName ?? '');
                row.push(Number(val.contaminantWeightPct).toFixed(3));
                row.push('');
                row.push(val.residualOilUncontaminatedWeightPct == null ? null : Number(val.residualOilUncontaminatedWeightPct).toFixed(3));
                row.push(val.residualOilUncontaminatedMolePct == null ? null : Number(val.residualOilUncontaminatedMolePct).toFixed(3));
                row.push('');
                row.push(val.recombinedFluidUncontaminatedWeightPct == null ? null : Number(val.recombinedFluidUncontaminatedWeightPct).toFixed(3));
                row.push(val.recombinedFluidUncontaminatedMolePct == null ? null : Number(val.recombinedFluidUncontaminatedMolePct).toFixed(3));

                formattedRows.push(row);
            });
        }

        return formattedRows;
    }

    private formatPercentageDisplay(value: number | null | undefined): string {
        if (value == null) return '';
        return `${Number.isInteger(value) ? value.toFixed(1) : value}%`;
    }
}